import React from 'react';
import { createPortal } from 'react-dom';
import { Input } from '@heroui/react';
import { fetchData } from '../services/api';

interface Suggestion {
  value: string;
  postal_code?: string;
  region?: string;
  city?: string;
  street?: string;
  house?: string;
  flat?: string;
  lat?: string | number;
  lon?: string | number;
  raw?: any;
}

interface Props {
  placeholder?: string;
  minLength?: number;
  onSelect: (s: Suggestion) => void;
  /** Тип подсказок: full address, city, street или house */
  type?: 'address' | 'city' | 'street' | 'house';
  /** Ограничение поиска (например, выбранный город или улица) */
  restrict?: string;
  /** Заблокировать поле (disabled) */
  disabled?: boolean;
  /** Optional custom suggest endpoint (path passed to fetchData). If provided, component will call `${suggestUrl}?name=...&full=1` */
  suggestUrl?: string;
  /** Optional container to render the dropdown into. If not provided, falls back to document.body */
  portalRoot?: HTMLElement | null;
  /** When true, render dropdown inline (no portal). Useful inside modals to keep dropdown inside modal DOM. */
  disablePortal?: boolean;
  /** Prefill input with an externally provided value (e.g. restored city). */
  initialValue?: string;
}

export const AddressAutocomplete: React.FC<Props> = ({ placeholder, minLength = 3, onSelect, type = 'address', restrict, disabled = false, suggestUrl, portalRoot, disablePortal = false, initialValue }) => {
  const defaultPlaceholder = type === 'city' ? 'Город' : type === 'street' ? 'Улица' : type === 'house' ? 'Дом' : 'Город, улица, дом';
  const usedPlaceholder = placeholder || defaultPlaceholder;
  const [query, setQuery] = React.useState('');
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [highlight, setHighlight] = React.useState(0);
  const debounceRef = React.useRef<number | null>(null);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties | null>(null);
  const ignoreNextQueryRef = React.useRef(false);
  // remember last selected display value so we can ignore re-fetches when the input equals it
  const lastSelectedValueRef = React.useRef<string | null>(null);
  // when true, skip fetches until user manually edits the input (cleared in handleQueryChange)
  const skipFetchUntilEditRef = React.useRef(false);

  // Sync input with provided initial value so persisted selections appear after reloads
  React.useEffect(() => {
    if (typeof initialValue === 'string' && initialValue !== query) {
      ignoreNextQueryRef.current = true;
      lastSelectedValueRef.current = initialValue;
      setQuery(initialValue);
      setSuggestions([]);
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue]);

  React.useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    // If we just selected a suggestion and want to skip further fetches until user edits, bail out
    if (skipFetchUntilEditRef.current) {
      // if the query matches the selected value keep dropdown closed and suggestions empty
      if (lastSelectedValueRef.current && query === lastSelectedValueRef.current) {
        setSuggestions([]);
        setOpen(false);
        return;
      }
      // if query changed (user typed), clear the skip marker and allow fetching
      skipFetchUntilEditRef.current = false;
      lastSelectedValueRef.current = null;
    }
    // If the current query equals the last selected value, skip fetching again (prevents duplicate fetch after select)
    if (lastSelectedValueRef.current && query === lastSelectedValueRef.current) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    // If we just selected a suggestion, skip one fetch triggered by setQuery in selectSuggestion
    if (ignoreNextQueryRef.current) {
      ignoreNextQueryRef.current = false;
      return;
    }
    if (!query || query.length < minLength) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
        try {
        // Используем общий API клиент (baseURL берётся из src/services/config.ts)
        // Параметры type/restrict передаём в backend proxy, который может транслировать их в Dadata
        let json: any = null;
        // If suggestUrl is provided, call it with ?name=...&full=1
        // Otherwise fallback to default address/suggest
        if (suggestUrl) {
          // If using the API address suggest proxy endpoints, they expect `q` and `count` params.
          // For legacy providers (e.g. CDEK proxy) keep using `name`/`full` to avoid breaking them.
          let url: string;
          if (suggestUrl.includes('/address/suggest')) {
            url = `${suggestUrl}?q=${encodeURIComponent(query)}&count=7`;
          } else {
            url = `${suggestUrl}?name=${encodeURIComponent(query)}&full=1`;
          }
          json = await fetchData(url);
        } else {
          let url = `/address/suggest?q=${encodeURIComponent(query)}`;
          if (type) url += `&type=${encodeURIComponent(type)}`;
          if (restrict) url += `&restrict=${encodeURIComponent(restrict)}`;
          json = await fetchData(url);
        }

        // Normalize different response shapes from various suggest providers (DaData, CDEK proxy, etc.)
        let suggestionsList: any[] = [];
        if (!json) {
          suggestionsList = [];
        } else if (Array.isArray(json)) {
          suggestionsList = json;
        } else if (Array.isArray(json.suggestions)) {
          suggestionsList = json.suggestions;
        } else if (Array.isArray(json.body)) {
          suggestionsList = json.body;
        } else if (Array.isArray(json.results)) {
          suggestionsList = json.results;
        } else if (json.data && Array.isArray(json.data)) {
          suggestionsList = json.data;
        } else if (json.data && Array.isArray(json.data.suggestions)) {
          suggestionsList = json.data.suggestions;
        } else {
          // Try to find any top-level array-ish field
          const arrField = ['suggestions', 'body', 'results', 'data'].find(f => Array.isArray((json as any)[f]));
          if (arrField) suggestionsList = (json as any)[arrField];
          else suggestionsList = [];
        }

        // Normalize suggestions into a uniform shape so display and selection work
        // with different provider responses (DaData, CDEK proxy, etc.).
        const normalized = (suggestionsList || []).map((it: any) => {
          // keep original under `raw` for consumers that expect provider fields
          const raw = it;
          // Prefer provider-specific display fields for street/house types per contract
          let value: string = '';
          if (type === 'street') {
            value = it.street_with_type || it.data?.street_with_type || it._raw?.data?.street_with_type || it.value || it.name || it.full_name || it.display_name || '';
          } else if (type === 'house') {
            // Build house string: house_type + house + block_type + block
            const houseType = it.house_type || it.data?.house_type || it._raw?.data?.house_type || it.type || '';
            const houseNum = it.house || it.data?.house || it._raw?.data?.house || it.value || it.number || '';
            const blockType = it.block_type || it.data?.block_type || it._raw?.data?.block_type || it.building_type || '';
            const block = it.block || it.data?.block || it._raw?.data?.block || it.building || '';
            const parts: string[] = [];
            if (houseType) parts.push(houseType);
            if (houseNum) parts.push(houseNum);
            if (blockType) parts.push(blockType);
            if (block) parts.push(block);
            value = parts.join(' ').trim();
            if (!value) value = it.full_name || it.value || it.name || it.label || '';
          } else {
            value = it.full_name || it.value || it.name || it.label || it.unrestricted_value || it.address || it.display_name || '';
          }
          return { ...it, value, raw } as Suggestion;
        });

        setSuggestions(normalized);
        setOpen(normalized.length > 0);
        setHighlight(0);
      } catch (e) {
        // При ошибке оставляем suggestions пустыми — UX не ломается
        setSuggestions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, [query, minLength, type, restrict]);

  // compute dropdown position when open or on resize/scroll
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!open) {
      setDropdownStyle(null);
      return;
    }

    const updatePos = () => {
      const el = wrapperRef.current;
      if (!el) return setDropdownStyle(null);
      const r = el.getBoundingClientRect();
      if (disablePortal) {
        // position relative to wrapper (rendered inline)
        setDropdownStyle({ position: 'absolute', top: el.offsetHeight, left: 0, width: el.offsetWidth, zIndex: 9999 });
      } else {
        // position in viewport (rendered into portalRoot or document.body)
        setDropdownStyle({ position: 'absolute', top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width, zIndex: 9999 });
      }
    };

    updatePos();
    window.addEventListener('resize', updatePos);
    window.addEventListener('scroll', updatePos, true);
    return () => {
      window.removeEventListener('resize', updatePos);
      window.removeEventListener('scroll', updatePos, true);
    };
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const s = suggestions[highlight];
      if (s) selectSuggestion(s);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const selectSuggestion = (s: Suggestion) => {
    const display = (s as any).value || (s as any).name || (s as any).label || s.raw?.full_name || (s as any)._raw?.full_name || (s as any).city || (s as any).region || '';
    // set the query but prevent the next effect from refetching immediately
    ignoreNextQueryRef.current = true;
    // remember what we selected so future re-renders/fetches won't refire for the same value
    lastSelectedValueRef.current = display as string;
    // skip further fetches until the user edits the input to avoid duplicate requests
    skipFetchUntilEditRef.current = true;
    setQuery(display as string);
    setOpen(false);
    setSuggestions([]);
    onSelect(s);
  };

  // Clear lastSelectedValueRef when the user types a different query
  const handleQueryChange = (v: string) => {
    // User started editing: clear any selection-locked state so searching resumes
    if (lastSelectedValueRef.current && v !== lastSelectedValueRef.current) {
      lastSelectedValueRef.current = null;
    }
    if (skipFetchUntilEditRef.current) skipFetchUntilEditRef.current = false;
    setQuery(v);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        value={query}
        onValueChange={handleQueryChange}
        placeholder={usedPlaceholder}
        className="w-full"
        onKeyDown={handleKeyDown}
        disabled={Boolean(disabled)}
      />

      {open && dropdownStyle && (
        disablePortal ? (
          <div style={dropdownStyle} className="bg-background border border-divider rounded shadow-lg max-h-64 overflow-auto">
            {loading && <div className="p-3 text-sm text-foreground/60">Загрузка...</div>}
            {!loading && suggestions.length === 0 && <div className="p-3 text-sm text-foreground/60">Нет вариантов</div>}
                {!loading && suggestions.map((s: any, i: number) => (
              <button
                key={i}
                onClick={() => selectSuggestion(s)}
                onMouseEnter={() => setHighlight(i)}
                className={`w-full text-left p-3 hover:bg-content3 ${i === highlight ? 'bg-content3' : ''}`}
              >
                    <div className="text-sm font-medium">{(s as any).value || (s as any).name || (s as any).label || s.raw?.full_name || s._raw?.full_name}</div>
                    <div className="text-xs text-foreground/60">{(s as any).postal_code ? `${(s as any).postal_code} · ` : ''}{(s as any).city || (s as any).region || s._raw?.full_name || (s as any).address || (s as any).label || ''}</div>
              </button>
            ))}
          </div>
        ) : (
          createPortal(
            <div style={dropdownStyle} className="bg-background border border-divider rounded shadow-lg max-h-64 overflow-auto">
              {loading && <div className="p-3 text-sm text-foreground/60">Загрузка...</div>}
              {!loading && suggestions.length === 0 && <div className="p-3 text-sm text-foreground/60">Нет вариантов</div>}
              {!loading && suggestions.map((s: any, i: number) => (
                <button
                  key={i}
                  onClick={() => selectSuggestion(s)}
                  onMouseEnter={() => setHighlight(i)}
                  className={`w-full text-left p-3 hover:bg-content3 ${i === highlight ? 'bg-content3' : ''}`}
                >
                  <div className="text-sm font-medium">{(s as any).value || (s as any).name || (s as any).label || s.raw?.full_name || s._raw?.full_name}</div>
                  <div className="text-xs text-foreground/60">{(s as any).postal_code ? `${(s as any).postal_code} · ` : ''}{(s as any).city || (s as any).region || s._raw?.full_name || (s as any).address || (s as any).label || ''}</div>
                </button>
              ))}
            </div>,
            // render into provided portalRoot when available to keep dropdown inside modals
            (portalRoot && portalRoot instanceof HTMLElement) ? portalRoot : document.body
          )
        )
      )}
    </div>
  );
};

export default AddressAutocomplete;
